import React, { forwardRef, useEffect, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { toast } from 'react-toastify'
import { invoiceApi, invoiceError } from '../../invoicing/api'
import PopupDropdown from '../../PopupDropdown'
import './PriceSection.css'

const PriceSection = forwardRef(({ building }, ref) => {
  const [schedules, setSchedules] = useState([])
  const [accounts, setAccounts] = useState([])
  const [accountCode, setAccountCode] = useState(null)
  const [accountLocked, setAccountLocked] = useState(false)
  const [accountSaving, setAccountSaving] = useState(false)
  const [busy, setBusy] = useState(false)
  const [dragSource, setDragSource] = useState(null)
  const [dragTarget, setDragTarget] = useState(null)

  useEffect(() => {
    if (!building.id) return
    setBusy(true)
    invoiceApi(`/buildings/${building.id}/payment-schedules`, null, 'get')
      .then(data => { setSchedules(data.schedules); setAccountCode(data.invoice_account_code || null); setAccountLocked(!!data.invoice_account_locked) })
      .catch(e => toast.error(invoiceError(e))).finally(() => setBusy(false))
    invoiceApi('/invoices/accounts', null, 'get')
      .then(data => setAccounts(data.accounts || [])).catch(e => toast.error(invoiceError(e)))
  }, [building.id])

  const updateStages = (scheduleIndex, updater) => {
    setSchedules(previous => previous.map((schedule, index) => index === scheduleIndex
      ? { ...schedule, stages: updater(schedule.stages) } : schedule))
  }
  const change = (scheduleIndex, stageIndex, field, value) => {
    updateStages(scheduleIndex, stages => stages.map((stage, index) => index === stageIndex ? { ...stage, [field]: value } : stage))
  }
  const move = (scheduleIndex, from, to) => {
    if (from === to || from < 0 || to < 0) return
    updateStages(scheduleIndex, stages => {
      if (from >= stages.length || to >= stages.length) return stages
      const next = [...stages]
      next.splice(to, 0, next.splice(from, 1)[0])
      return next
    })
  }
  const save = async schedule => {
    setBusy(true)
    try {
      const payload = { ...schedule, stages: schedule.stages.map((stage, index) => ({ ...stage, stage_number: index + 1 })) }
      const data = await invoiceApi(`/buildings/${building.id}/payment-schedules`, payload, 'put')
      setSchedules(previous => previous.map(item => item.unit_type === schedule.unit_type
        ? data.schedules.find(next => next.unit_type === item.unit_type) : item))
      toast.success(`${schedule.unit_type}: instalments saved.`)
    } catch (e) { toast.error(invoiceError(e)) } finally { setBusy(false) }
  }
  const saveAccount = async account => {
    const nextAccountCode = account?.id || null
    if (!nextAccountCode || nextAccountCode === accountCode) return
    const previousAccountCode = accountCode
    setAccountCode(nextAccountCode)
    setAccountSaving(true)
    try {
      const data = await invoiceApi(`/buildings/${building.id}/payment-schedules`, { invoice_account_code: nextAccountCode }, 'put')
      setAccountCode(data.invoice_account_code || null)
      setAccountLocked(!!data.invoice_account_locked)
      toast.success('Issuing company saved.')
    } catch (e) {
      setAccountCode(previousAccountCode)
      toast.error(invoiceError(e))
    } finally { setAccountSaving(false) }
  }

  return <div className="details_container building-price-details" id="building-price-section">
    <div className="panel"><div className="body__section"><h2>Instalments</h2>
      <div className="form__section no-border no-padding"><div className="form__section--content building-stages">
        <div className="building-invoice-company">
          <label>Issuing company</label>
          <PopupDropdown title={accounts.length ? 'Choose company' : 'No companies available'} options={accounts}
            value={accounts.find(account => account.id === accountCode) || null}
            onChange={saveAccount} disabled={busy || accountSaving || accountLocked || !accounts.length} searchBar={true} />
          {accountSaving && <small aria-live="polite">Saving…</small>}
          {accountLocked && <small>Locked after invoicing started.</small>}
        </div>
        {!schedules.length && <p>{busy ? 'Loading instalments…' : 'Add units to this building first.'}</p>}
        {schedules.map((schedule, scheduleIndex) => <details className="building-stage-category" key={schedule.unit_type}>
          <summary><span>{schedule.unit_type || 'Unspecified type'}</span><span>{schedule.stages.length} stages</span></summary>
          <div className="building-stage-content">
            <div className="building-stage-columns"><span></span><span>#</span><span>Description</span><span>%</span><span>Ready</span><span></span></div>
            {schedule.stages.map((stage, stageIndex) => <div className={`building-stage-row ${dragTarget === `${scheduleIndex}-${stageIndex}` ? 'drag-target' : ''}`}
              key={stage.id || `new-${stageIndex}`}
              onDragOver={event => {
                if (dragSource?.scheduleIndex !== scheduleIndex) return
                event.preventDefault()
                setDragTarget(`${scheduleIndex}-${stageIndex}`)
              }}
              onDragLeave={() => setDragTarget(null)}
              onDrop={event => {
                event.preventDefault()
                if (dragSource?.scheduleIndex === scheduleIndex) move(scheduleIndex, dragSource.stageIndex, stageIndex)
                setDragSource(null)
                setDragTarget(null)
              }}>
              <button type="button" className="stage-icon-button drag-handle" draggable={!busy && !schedule.structure_locked} disabled={busy || schedule.structure_locked} title="Drag to reorder"
                aria-label={`Drag stage ${stageIndex + 1} to reorder`}
                onDragStart={event => {
                  setDragSource({ scheduleIndex, stageIndex })
                  event.dataTransfer.setData('text/plain', `${scheduleIndex}:${stageIndex}`)
                  event.dataTransfer.effectAllowed = 'move'
                }}
                onDragEnd={() => { setDragSource(null); setDragTarget(null) }}><DragIndicatorIcon /></button>
              <span className="stage-number">{stageIndex + 1}</span>
              <input aria-label={`Stage ${stageIndex + 1} description`} className="input_field__input_box--text" value={stage.stage_name} disabled={busy || schedule.structure_locked}
                onChange={event => change(scheduleIndex, stageIndex, 'stage_name', event.target.value)} />
              <input type="number" aria-label={`Stage ${stageIndex + 1} percentage`} className="input_field__input_box--text" min="0" max="100" step="0.000001"
                value={stage.payment_percentage} disabled={busy || schedule.structure_locked} onChange={event => change(scheduleIndex, stageIndex, 'payment_percentage', event.target.value)} />
              <label className="stage-ready"><input type="checkbox" checked={!!stage.ready} disabled={busy}
                onChange={event => change(scheduleIndex, stageIndex, 'ready', event.target.checked)} /><span>Ready</span></label>
              <button type="button" className="stage-icon-button remove-stage" title="Remove stage" aria-label={`Remove stage ${stageIndex + 1}`}
                disabled={busy || schedule.structure_locked} onClick={() => updateStages(scheduleIndex, stages => stages.filter((_, index) => index !== stageIndex))}><DeleteOutlineIcon /></button>
            </div>)}
            {schedule.structure_locked && <p className="building-stage-lock-note">Descriptions, percentages, and order are locked after invoicing. Readiness remains editable.</p>}
            <div className="building-stage-footer">
              <button type="button" className="stage-add-button" disabled={busy || schedule.structure_locked} onClick={() => updateStages(scheduleIndex, stages =>
                [...stages, { stage_number: stages.length + 1, stage_name: '', payment_percentage: 0, ready: false }])}><AddIcon /> Add stage</button>
              <span className={Math.abs(schedule.stages.reduce((total, stage) => total + Number(stage.payment_percentage || 0), 0) - 100) > 0.000001 ? 'invalid-total' : ''}>
                Total: {schedule.stages.reduce((total, stage) => total + Number(stage.payment_percentage || 0), 0).toFixed(2)}%</span>
              <button type="button" className="stage-save-button" disabled={busy || !schedule.stages.length} onClick={() => save(schedule)}>Save</button>
            </div>
          </div>
        </details>)}
      </div></div>
    </div></div>
  </div>
})

PriceSection.displayName = 'PriceSection'
export default PriceSection
